import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { NotificacionDto, NotificacionPagedResponse } from '../models/notificacion-dto.model';
import {
  NotifPorEstadoFilterRequest,
  NotificadosFilterRequest,
  NotificadosIndicadorResult,
} from '../models/notificacion-indicador-filtro-request.model';
import { ApiResponse } from '../models/api-response.model';
import { parseDisplayDateTime } from '../utils/date.utils';
import {
  BatchDeleteRequest,
  BatchDeleteResponse,
  ResendMasivoRequest,
  ResendMasivoResponse,
} from '../models/notificaciones-listado-acciones.model';
import { NotificacionesFilterParams } from '../../features/notificaciones/listado-notificaciones/models/notificaciones-filter.model';
import { ConfigClientService } from './configClient.service';
import { SINERGIAL_ROUTES } from '../constants/api-sinergial-routes';
import { CONTEXT } from '../models/context';
import { BaseApiService } from './api-base.service';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class NotificacionesListadoApiService extends BaseApiService<BatchDeleteResponse> {
  private readonly INTEGRA_HEADERS = {
    'Accept-Language': 'es-AR',
    'X-Verificar-Permisos': 'S',
  } as const;

  constructor(
    http: HttpClient,
    configService: ConfigService,
    configClienteService: ConfigClientService,
    authService: AuthService,
  ) {
    super(http, configService, configClienteService, authService);
  }

  /**
   * Cantidad y tiempo promedio (FechaNotificado - FechaNotificacion) de las
   * notificaciones ya notificadas ('Estados: N') para el período dado.
   */
  async indNotificados(filtro: NotificadosFilterRequest): Promise<NotificadosIndicadorResult> {
    const endpoint = this.resolveEndpoint(true);
    const payload = await firstValueFrom(
      this.http.get<ApiResponse<unknown>>(endpoint, {
        params: this.buildIndicadorParams(filtro),
      }),
    );

    const registros = this.extractRecords(payload);
    const cantidad = registros.length;
    const tiempoPromedioMs =
      cantidad > 0
        ? registros.reduce((acc, registro) => acc + this.diffNotificacionMs(registro), 0) / cantidad
        : 0;

    return { cantidad, tiempoPromedioMs };
  }

  private diffNotificacionMs(registro: NotificacionDto): number {
    if (!registro.FechaNotificacion || !registro.FechaNotificado) {
      return 0;
    }
    const desde = parseDisplayDateTime(registro.FechaNotificacion).getTime();
    const hasta = parseDisplayDateTime(registro.FechaNotificado).getTime();
    return Math.max(0, hasta - desde);
  }

  /** Conteo por cada estado de notificación, en paralelo. */
  async indNotifPorEstado(
    filtro: Omit<NotifPorEstadoFilterRequest, 'Estados'>,
    estados: readonly NotifPorEstadoFilterRequest['Estados'][],
  ): Promise<Record<string, number>> {
    const endpoint = this.resolveEndpoint(true);
    const requests = estados.map((estado) =>
      firstValueFrom(
        this.http.get<ApiResponse<unknown>>(endpoint, {
          params: this.buildIndicadorParams({ ...filtro, Estados: estado }),
        }),
      ),
    );

    const responses = await Promise.all(requests);
    return estados.reduce<Record<string, number>>((acc, estado, index) => {
      acc[estado] = responses[index]?.TotalRecords ?? 0;
      return acc;
    }, {});
  }

  private buildIndicadorParams(
    filtro: NotificadosFilterRequest | NotifPorEstadoFilterRequest,
  ): HttpParams {
    const query: Record<string, string> = {
      PageNumber: String(filtro.PageNumber),
      IdsEmpresas: String(filtro.IdsEmpresas),
      Estados: filtro.Estados,
    };

    if ('PageSize' in filtro) {
      query['PageSize'] = String(filtro.PageSize);
    }

    appendIfPresent(query, 'FechaDesdeNotificacion', filtro.FechaDesdeNotificacion);
    appendIfPresent(query, 'FechaHastaNotificacion', filtro.FechaHastaNotificacion);

    return new HttpParams({ fromObject: query });
  }

  async loadListado(
    params: NotificacionesFilterParams & {
      readonly isHistorical: boolean;
    },
  ): Promise<NotificacionPagedResponse> {
    const endpoint = this.resolveEndpoint(params.isHistorical);
    const payload = await firstValueFrom(
      this.http.get<NotificacionPagedResponse>(endpoint, {
        params: this.buildParams(params),
      }),
    );
    return payload;
  }

  private resolveEndpoint(isHistorical: boolean): string {
    const path = isHistorical
      ? SINERGIAL_ROUTES.NOTIFICACION_HISTORIAL.PATH_BASE
      : SINERGIAL_ROUTES.NOTIFICACION.PATH_BASE;
    return this.resolveUrl(path);
  }

  private buildParams(
    params: NotificacionesFilterParams & { readonly isHistorical: boolean },
  ): HttpParams {
    const orderValue = params.Orden != null ? String(params.Orden) : '6';
    const sentidoValue = params.SentidoOrden ?? 'Desc';

    const query: Record<string, string> = {
      PageNumber: String(params.PageNumber),
      PageSize: String(params.PageSize),
      IncluirReceptores: params.IncluirReceptores ?? 'S',
      IncluirRegistros: params.IncluirRegistros ?? 'S',
      IdsEmpresas: String(params.IdsEmpresas),
      ...(params.isHistorical
        ? { Orden: orderValue, SentidoOrden: sentidoValue }
        : { Order: orderValue, SentidoOrden: sentidoValue }),
    };

    appendIfPresent(query, 'Estados', params.Estados?.join(','));
    appendIfPresent(query, 'IdsNotifConfiguraciones', params.IdsNotifConfiguraciones?.join(','));
    appendIfPresent(query, 'IdsNotifTiposReceptores', params.IdsNotifTiposReceptores?.join(','));
    appendIfPresent(query, 'IdsNotifCanales', params.IdsNotifCanales?.join(','));
    appendIfPresent(query, 'IdsNotifTipos', params.IdsNotifTipos?.join(','));
    appendIfPresent(query, 'IdsReceptoresConTipos', params.IdsReceptoresConTipos?.join(','));
    appendIfPresent(query, 'IdsNotifDominios', params.IdsNotifDominios?.join(','));
    appendIfPresent(query, 'IdsRegistrosConDominios', params.IdsRegistrosConDominios?.join(','));
    appendIfPresent(query, 'InformacionContacto', params.InformacionContacto);
    appendIfPresent(query, 'FechaDesdeRegistro', params.FechaDesdeRegistro);
    appendIfPresent(query, 'FechaHastaRegistro', params.FechaHastaRegistro);
    appendIfPresent(query, 'FechaDesdeNotificacion', params.FechaDesdeNotificacion);
    appendIfPresent(query, 'FechaHastaNotificacion', params.FechaHastaNotificacion);
    appendIfPresent(query, 'FechaDesdeNotificado', params.FechaDesdeNotificado);
    appendIfPresent(query, 'FechaHastaNotificado', params.FechaHastaNotificado);

    return new HttpParams({ fromObject: query });
  }

  /**
   * DELETE /Notificacion/BatchDelete — bulk delete notificaciones.
   * Sends IDs as a comma-separated string inside { IdsNotificacion }.
   */
  async batchDelete(ids: number[]): Promise<BatchDeleteResponse> {
    const body: BatchDeleteRequest = { IdsNotificacion: ids.join(',') };
    return await firstValueFrom(
      this.deleteWithBody<BatchDeleteResponse, BatchDeleteRequest>(
        SINERGIAL_ROUTES.NOTIFICACION.PATH_BASE +
          SINERGIAL_ROUTES.NOTIFICACION.BATCH_DELETE.PATH_REL,
        body,
      ),
    );
  }

  /**
   * DELETE /Notificacion/{id} — delete a single notificacion.
   */
  async deleteById(id: number): Promise<ApiResponse<unknown>> {
    return await firstValueFrom(
      this.delete<ApiResponse<unknown>>(SINERGIAL_ROUTES.NOTIFICACION.PATH_BASE + '/' + id),
    );
  }

  /**
   * POST /v3/notificaciones/envio-masivo — bulk resend notificaciones via integra endpoint.
   * Sends IDs as a JSON array inside { ids: [...] }.
   */
  async resendMasivo(ids: number[]): Promise<ResendMasivoResponse> {
    const baseUrl = this.configClienteService.getConfig()?.api_integra || '';
    const body: ResendMasivoRequest = { ids };

    const authIntegraRaw = localStorage.getItem('auth_integra');
    if (!authIntegraRaw) {
      throw new Error('No se encontró el token de API Integra. Inicie sesión nuevamente.');
    }

    const { token: integraToken } = JSON.parse(authIntegraRaw) as { token: string };
    const context = new HttpContext().set(CONTEXT, { noSendHeader: true });
    const headers = {
      ...this.INTEGRA_HEADERS,
      Authorization: `Bearer ${integraToken}`,
    };

    const payload = await firstValueFrom(
      this.http.post<ResendMasivoResponse>(`${baseUrl}/notificaciones/envio-masivo`, body, {
        context,
        headers,
      }),
    );
    return payload;
  }

  extractRecords(payload: NotificacionPagedResponse | unknown): readonly NotificacionDto[] {
    if (Array.isArray(payload)) {
      return payload.filter(this.isNotificacionDto);
    }

    if (!this.isObjectRecord(payload)) {
      return [];
    }

    const data = payload['Data'];
    if (Array.isArray(data)) {
      return data.filter(this.isNotificacionDto);
    }

    if (this.isObjectRecord(data)) {
      const nested = data['listado'] ?? data['items'] ?? data['notificaciones'] ?? data['Data'];
      if (Array.isArray(nested)) {
        return nested.filter(this.isNotificacionDto);
      }
    }

    const direct =
      payload['listado'] ?? payload['items'] ?? payload['notificaciones'] ?? payload['Data'];
    if (Array.isArray(direct)) {
      return direct.filter(this.isNotificacionDto);
    }

    return [];
  }

  private readonly isNotificacionDto = (value: unknown): value is NotificacionDto => {
    return this.isObjectRecord(value);
  };

  private readonly isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
  };
}

function appendIfPresent(
  query: Record<string, string>,
  key: string,
  value: string | undefined,
): void {
  if (value != null && value !== '') {
    query[key] = value;
  }
}
